import { useEffect, useState, createContext, useContext, lazy, Suspense, Fragment } from "react";
import { useActiveAccountSwitch } from "@/hooks/useActiveAccountSwitch";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import NewVersionToast from "./components/NewVersionToast";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

// Critical pages (loaded immediately)
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import InvitePage from "./pages/InvitePage";
import PublicBioPage from "./pages/PublicBioPage";
import NotFound from "./pages/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy-loaded pages (code-split)
// Lazy on purpose, not for bundle size. The modal imports `api` from
// THIS module, and `api` is an `export const` defined further down — a
// static import here would evaluate that module while App.js is still
// initialising, with `api` in its temporal dead zone. Deferring the
// import until render means App.js has finished by the time it runs.
const GrowingAccountsModal = lazy(
  () => import("./components/consent/GrowingAccountsModal"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const InboxPage = lazy(() => import("./pages/InboxPage"));
const AutomationPage = lazy(() => import("./pages/AutomationPage"));
const MultilingualAutomationPage = lazy(() => import("./pages/automation-multilingual"));
const ContactsPage = lazy(() => import("./pages/ContactsPage"));
const PostsReelsPage = lazy(() => import("./pages/PostsReelsPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
import ComingSoonPage from "./pages/ComingSoonPage";
// LOCKED — see config/comingSoon.js. Kept commented rather than
// deleted so unlocking is one line, and commented rather than left
// unused so an eslint-warnings-as-errors build (CI=true) still passes.
// const ABTestingPage = lazy(() => import("./pages/ABTestingPage"));
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const CommentsPage = lazy(() => import("./pages/CommentsPage"));
// /analytics picks the old or new page on the analytics_v4_ui switch;
// /analytics/v4 and /analytics/legacy name one each (see AnalyticsRoute).
const AnalyticsPage = lazy(() => import("./pages/analytics4/AnalyticsRoute"));
const AnalyticsLegacyPage = lazy(() =>
  import("./pages/analytics4/AnalyticsRoute").then((m) => ({ default: m.AnalyticsV3Page })));
const AnalyticsV4Page = lazy(() => import("./pages/analytics4/AnalyticsV4"));
const ReelDetailPage = lazy(() => import("./pages/analytics4/ReelDetailPage"));
const AIReportPage = lazy(() =>
  import("./pages/analytics4/ReelDetailPage").then((m) => ({ default: m.AIReportPage })));
// Analytics AI v5 report pages (components live in pages/analytics4/v5).
const AIV5ReelReportPage = lazy(() => import("./pages/analytics4/v5/reports").then((m) => ({ default: m.ReelReportPage })));
const AIV5WeeklyReportPage = lazy(() => import("./pages/analytics4/v5/reports").then((m) => ({ default: m.WeeklyReportPage })));
const DebugInstagramPage = lazy(() => import("./pages/DebugInstagramPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const BioEditorPage = lazy(() => import("./pages/BioEditorPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const InstagramConnectPage = lazy(() => import("./pages/InstagramConnectPage"));
const ManageAccountsPage = lazy(() => import("./pages/ManageAccountsPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
// LOCKED — see config/comingSoon.js. Kept commented rather than
// deleted so unlocking is one line, and commented rather than left
// unused so an eslint-warnings-as-errors build (CI=true) still passes.
// const AIWriterPage = lazy(() => import("./pages/AIWriterPage"));
const GrowthAgentPage = lazy(() => import("./pages/GrowthAgentPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const DataDeletionPage = lazy(() => import("./pages/DataDeletionPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const BlogAdminLoginPage = lazy(() => import("./pages/BlogAdminLoginPage"));
const BlogAdminPage = lazy(() => import("./pages/BlogAdminPage"));
const TrendsPage = lazy(() => import("./pages/TrendsPage"));
const CreatorMarketplacePage = lazy(() => import("./pages/CreatorMarketplacePage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const NotificationPrefsPage = lazy(() => import("./pages/NotificationPrefsPage"));
const MasterApp = lazy(() => import("./master/MasterApp"));
import ImpersonationBanner from "./components/ImpersonationBanner";
import InstagramReconnectBanner from "./components/InstagramReconnectBanner";
import RobotsPolicy from "./components/RobotsPolicy";
import { FeatureAccessProvider } from "./hooks/useFeatureAccess";
const AutomationResultsPage = lazy(() => import("./pages/AutomationResultsPage"));
const MarketingPagePlaceholder = lazy(() => import("./pages/MarketingPagePlaceholder"));
const StandalonePage = lazy(() => import("./pages/StandalonePage"));
import MarketingNav from "./components/marketing/MarketingNav";
const ToolComparisonPage = lazy(() => import("./pages/ToolComparisonPage"));
const FeaturesIndexPage = lazy(() => import("./pages/FeaturesIndexPage"));
import { STANDALONE_SLUGS } from "./data/standalonePages";
const FeaturePage = lazy(() => import("./pages/FeaturePage"));
const UseCasePage = lazy(() => import("./pages/UseCasePage"));
const LearnIndexPage = lazy(() => import("./pages/LearnIndexPage"));
const LearnPage = lazy(() => import("./pages/LearnPage"));
const MultilingualSeoPage = lazy(() => import("./pages/multilingual/MultilingualSeoPage"));
import { SLUGS as MULTILINGUAL_SEO_SLUGS } from "./content/multilingual/slugs";
const ComparePage = lazy(() => import("./pages/ComparePage"));
const CompareIndexPage = lazy(() => import("./pages/CompareIndexPage"));
const EditorListPage = lazy(() => import("./features/editor/EditorListPage"));
const EditorPage = lazy(() => import("./features/editor/EditorPage"));

// Components
import MobileNav from "./components/MobileNav";
import DesktopSidebar from "./components/DesktopSidebar";
import { SupportProvider } from "./context/SupportContext";
import OnboardingProvider from "./components/OnboardingProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

// API helper with auth
export const api = axios.create({
  baseURL: API,
});

// Track refresh attempts to prevent infinite loops
let isRefreshing = false;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("tadlo_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Let Instagram Graph API errors through — the backend intentionally
    // returns 401 with `detail.code === 'ig_token_invalid'` so that the
    // Comments/Inbox pages can render the reconnect card. A blanket 401
    // → logout here would strip the token and bounce to /auth before
    // React ever sees the response (defeats the whole point of the
    // friendly reconnect UX).
    const detail = error.response?.data?.detail;
    const isIgGraphError =
      detail && typeof detail === "object" && typeof detail.code === "string" && detail.code.startsWith("ig_");
    if (isIgGraphError) {
      // Tag the rejection so callers can branch without parsing the
      // detail blob themselves.
      error.isTokenExpired = detail.code === "ig_token_invalid";
      error.igErrorCode = detail.code;
      // Platform-wide red banner: dispatch ONCE for ig_token_invalid.
      // The banner component listens for this event and renders the
      // persistent "Reconnect Instagram" CTA at the top of every page.
      if (detail.code === "ig_token_invalid" && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("ig_token_expired", {
          detail: {
            message: detail.message ||
              "Your Instagram session has expired. Please reconnect your account.",
          },
        }));
      }
      return Promise.reject(error);
    }

    // If 401 error and we haven't tried refreshing yet
    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshing) {
      originalRequest._retry = true;
      
      // Skip redirect entirely for the Master Admin SPA and the
      // BlogAdmin SPA — both manage their own auth (blog_admin_token
      // lives in localStorage under its own key and is unrelated to
      // the tadlo_token user session). Without this guard, a stray
      // 401 from any shared axios call (e.g. /auth/me fired by a
      // common hook) redirects the admin out to /auth.
      const isMasterRoute = window.location.pathname.startsWith('/master');
      const isBlogAdminRoute = window.location.pathname.startsWith('/td-admin');

      // Check if this is from the auth/me endpoint - if so, just clear and redirect
      if (originalRequest.url?.includes('/auth/me')) {
        localStorage.removeItem("tadlo_token");
        localStorage.removeItem("tadlo_user");
        // Only redirect if not already on auth page and not in master/blog-admin SPA
        if (!window.location.pathname.includes('/auth') && !isMasterRoute && !isBlogAdminRoute) {
          window.location.href = "/auth";
        }
        return Promise.reject(error);
      }
      
      // For other endpoints, clear the session and redirect
      isRefreshing = true;
      localStorage.removeItem("tadlo_token");
      localStorage.removeItem("tadlo_user");
      
      // Only redirect if not on public routes (master + blog-admin SPAs self-manage auth).
      const publicRoutes = [
        '/auth', '/privacy', '/terms', '/verify',
        '/master', '/td-admin', '/td-admin-auth',
        '/blog',
        // Aug 2026 — public marketing surface. These pages are crawled and
        // prerendered (see frontend/scripts/prerender.js) and MUST render for
        // anonymous visitors.
        //
        // This is load-bearing, not defensive: FeatureAccessProvider wraps
        // every route and fires GET /api/features/access on mount with no
        // token check. That endpoint 401s for anonymous callers, and the
        // branch below turns any non-public 401 into
        // `window.location.href = "/auth"`. Leave a marketing path out of
        // this list and a logged-out reader — and every crawler that does
        // run JS — gets bounced to the login screen.
        '/pricing', '/subscription', '/features', '/compare', '/use-cases',
        // Oct 2026 — the /learn answer pages and their hub.
        '/learn',
        '/data-deletion', '/signup', '/login', '/trends',
        // Sep 2026 — top-level marketing pages (src/data/standalonePages.js).
        // Adding one to RESERVED_PATHS is what makes it NEED to be here:
        // before it is reserved it counts as a bio page and passes via
        // isBioPage; the moment it is reserved, that fallback stops applying.
        '/meta-approved',
        // May 2026 — public referral landing must NOT redirect
        // unauthenticated visitors to /auth (the whole point is
        // to show them the offer first).
        '/invite',
      ];
      // Aug 2026 — a PUBLIC BIO PAGE is public by definition.
      //
      // The list above can only ever enumerate app routes, so every
      // tadlo.ai/{slug} bio page fell through it and any stray 401 ejected
      // the visitor to /auth. That was the live behaviour: opening
      // tadlo.ai/piyushnaik logged out redirected to the login screen, on
      // the one page type whose whole purpose is being publicly viewable.
      //
      // isReservedPath() is the SAME predicate the router uses to decide
      // "app route vs. bio slug" (see RESERVED_PATHS), so anything it does
      // not claim is a bio page and must never be redirected away from.
      // Keeping one predicate means a new reserved path can't be added to
      // the router and forgotten here.
      const path = window.location.pathname;
      const isBioPage = !isReservedPath(path);
      const isPublicRoute =
        isBioPage ||
        path === '/' ||
        publicRoutes.some(route => path === route || path.startsWith(route));

      if (!isPublicRoute) {
        window.location.href = "/auth";
      }
      
      isRefreshing = false;
    }
    return Promise.reject(error);
  }
);

// Theme Provider
function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem("tadlo_theme");
    if (stored) return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("tadlo_theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("tadlo_user");
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Impersonation token in URL? Use it (ephemeral, not persisted).
      const params = new URLSearchParams(window.location.search);
      const impToken = params.get("impersonation_token");
      const impSid = params.get("impersonation_session_id");
      if (impToken) {
        // Write to localStorage so the rest of the app picks it up,
        // but mark it as impersonation so the banner shows and we never
        // persist it across a clean logout.
        localStorage.setItem("tadlo_token", impToken);
        sessionStorage.setItem("tadlo_impersonation", "1");
        if (impSid) sessionStorage.setItem("tadlo_impersonation_sid", impSid);
        // Strip the query params from the URL bar.
        params.delete("impersonation_token");
        params.delete("impersonation_session_id");
        const newUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "") + window.location.hash;
        window.history.replaceState(null, "", newUrl);
      }
      const token = localStorage.getItem("tadlo_token");
      if (token) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
          localStorage.setItem("tadlo_user", JSON.stringify(response.data));
          // Feb 2026 — stamp a freshness marker so cold-boot retries
          // can decide whether the cached user is still trustworthy.
          localStorage.setItem("tadlo_user_cache_time", String(Date.now()));
        } catch (error) {
          // Feb 2026 — Cold-boot resilience. If the backend is still
          // warming up (deploy window, Wave-2 starvation, transient
          // 502/504), ``/auth/me`` can legitimately fail for a few
          // seconds even though the user's token is still valid. Use
          // the cached user for up to 60 seconds post-last-success so
          // we don't bounce them to ``/auth``. A background reload
          // fires after 15s to pick up real auth as soon as the
          // backend recovers.
          const cachedUser = localStorage.getItem("tadlo_user");
          const cacheTimeStr = localStorage.getItem("tadlo_user_cache_time") || "0";
          const cacheAgeMs = Date.now() - parseInt(cacheTimeStr, 10);
          const networkLike = !error?.response
            || error?.response?.status >= 500
            || error?.code === "ECONNABORTED";
          if (cachedUser && cacheAgeMs < 60000 && networkLike) {
            try {
              setUser(JSON.parse(cachedUser));
              setTimeout(() => {
                try { window.location.reload(); } catch (_) { /* noop */ }
              }, 15000);
              setLoading(false);
              return;
            } catch (_) {
              // Fall through to the hard-logout path if cached payload
              // is malformed.
            }
          }
          localStorage.removeItem("tadlo_token");
          localStorage.removeItem("tadlo_user");
          localStorage.removeItem("tadlo_user_cache_time");
          sessionStorage.removeItem("tadlo_impersonation");
          sessionStorage.removeItem("tadlo_impersonation_sid");
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = (userData, token) => {
    localStorage.setItem("tadlo_token", token);
    localStorage.setItem("tadlo_user", JSON.stringify(userData));
    localStorage.setItem("tadlo_user_cache_time", String(Date.now()));
    setUser(userData);
    // FeatureAccessProvider sits ABOVE BrowserRouter, so it never remounts on
    // navigation, and it now skips its fetch entirely when no token is
    // present. Tell it the token just arrived, or the feature map stays empty
    // until the 60s refresh interval fires.
    try {
      window.dispatchEvent(new CustomEvent("tadlo:auth-changed"));
    } catch { /* no window */ }
    // May 2026 — post-signup referral activation. If the user
    // arrived via a /invite/{TOKEN} landing, the token was stashed
    // before the auth handoff. Activate it now (idempotent on
    // backend) so the granted plan kicks in before the dashboard
    // loads any feature-gated UI. Best-effort — failures shouldn't
    // block the login; the token stays in localStorage so a manual
    // retry from the user is still possible. Cleared on success.
    try {
      const pending = localStorage.getItem("pending_referral_token");
      if (pending) {
        api.post("/referral/activate", { token: pending })
          .then((res) => {
            try {
              const planLabel = (res?.data?.plan || "").charAt(0).toUpperCase() + (res?.data?.plan || "").slice(1);
              const expiresAt = res?.data?.expires_at
                ? new Date(res.data.expires_at) : null;
              const months = expiresAt
                ? Math.max(1, Math.round((expiresAt - Date.now()) / (1000 * 60 * 60 * 24 * 30)))
                : null;
              // Lazy-import sonner to avoid a hard dep on context order
              import("sonner").then(({ toast }) => {
                toast.success(
                  `🎁 Your ${planLabel} plan has been activated${months ? ` for ${months} month${months > 1 ? "s" : ""}` : ""}!`,
                );
              }).catch(() => {});
            } catch { /* toast best-effort */ }
            localStorage.removeItem("pending_referral_token");
          })
          .catch(() => {
            // Wrong token / already used / expired — drop it so we
            // don't keep retrying on every login.
            localStorage.removeItem("pending_referral_token");
          });
      }
    } catch { /* localStorage unavailable */ }
  };

  const logout = () => {
    localStorage.removeItem("tadlo_token");
    localStorage.removeItem("tadlo_user");
    setUser(null);
    // Symmetric with login(): drops the cached feature map so a signed-out
    // session cannot keep showing unlocked UI.
    try {
      window.dispatchEvent(new CustomEvent("tadlo:auth-changed"));
    } catch { /* no window */ }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * ExternalRedirect — navigates via `window.location` (NOT React Router) so
 * the browser actually fetches the target URL from the server and renders
 * the raw response (XML, text, etc). Used for /sitemap.xml → /api/sitemap.xml
 * and /robots.txt → /api/robots.txt because Kubernetes ingress only routes
 * /api/* to the backend.
 */
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}


// Protected Route
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <div className="w-12 h-12 rounded-full bg-primary/20"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
}

// Reserved app paths (these are NOT bio pages)
const RESERVED_PATHS = [
  "/", "/auth", "/dashboard", "/inbox", "/automation", "/ab-testing",
  "/leads", "/comments", "/analytics", "/bio", "/settings", "/pricing", "/contacts",
  "/connect-instagram", "/instagram", "/ai-writer", "/growth-agent", "/privacy", "/terms", "/data-deletion",
  "/blog", "/td-admin-auth", "/td-admin", "/trends", "/posts", "/notifications", "/verify",
  "/subscription", "/collabs",
  "/invite",
  // Top-level marketing pages (src/data/standalonePages.js). Must match
  // RESERVED_SLUGS in backend/routes/slice_bio_pages.py — both halves of the
  // contract, or check-reserved-slugs.js fails the build.
  "/meta-approved",
  // Use-case pages live at /use-cases/:slug. Reserving the PARENT is what
  // stops a creator claiming the bio slug "use-cases" and shadowing them.
  "/use-cases",
  // The /learn hub and its seven answer pages. Unlike /use-cases this parent
  // IS a real page, so reserving it is doubly load-bearing: without it the
  // router would treat /learn as a creator's bio slug and the hub every
  // answer page names in its breadcrumb would never render. Must match
  // RESERVED_SLUGS in backend/routes/slice_bio_pages.py.
  "/learn",
  // Aug 2026 — these four were MISSING and every one of them was being
  // treated as a public bio slug: `isPublicBioPage = !isReservedPath(...)`,
  // so AppLayout returned `children` bare and the desktop sidebar and the
  // mobile bottom nav never mounted. /products shipped with no navigation
  // at all on either breakpoint (P, 2026-08-21); /editor and
  // /debug-instagram had the same hole and nobody had reported it yet.
  //
  // The chrome is the visible symptom. The load-bearing part is that
  // isReservedPath() also decides, at App.js:206, whether a path is a
  // creator's bio page — so an app route missing here is one a bio slug
  // could shadow.
  "/products", "/editor", "/debug-instagram", "/master",
  "/sitemap.xml", "/robots.txt",
  // SEO marketing page groups (scaffolded placeholders — content TBD)
  "/features", "/compare", "/signup", "/login"
];

function isReservedPath(pathname) {
  return RESERVED_PATHS.some(p => pathname === p || pathname.startsWith(p + "/"));
}

// App Layout with navigation
/**
 * Remount the whole routed view when the creator switches Instagram
 * accounts.
 *
 * ═══════════════════════════════════════════════════════════════════
 *  WHY A REMOUNT AND NOT A LISTENER PER PAGE
 * ═══════════════════════════════════════════════════════════════════
 * Five pages were wired to refetch on `ig-active-account-switched`, and
 * that only ever covers the pages somebody remembered. A creator can be
 * on Automation Results, a flow builder step, an analytics sub-page or
 * anything added next week, and every one of those would keep showing
 * the previous account's data with nothing admitting it.
 *
 * Changing the key on a Fragment unmounts the subtree and mounts a new
 * one, so EVERY screen re-runs its mount effects and fetches for the
 * account that is now selected — including screens that know nothing
 * about accounts.
 *
 * The cost is deliberate: in-progress edits are discarded on switch.
 * That is correct, because the thing being edited belongs to the account
 * the creator just navigated away from.
 *
 * It does NOT fire for single-account users: neither switch affordance
 * renders with one account, so the event is never dispatched.
 */
function RemountOnAccountSwitch({ children }) {
  const [nonce, setNonce] = useState(0);
  useActiveAccountSwitch(() => setNonce((n) => n + 1));
  return <Fragment key={nonce}>{children}</Fragment>;
}


function AppLayout({ children }) {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  // /auth, /signup and /login all render AuthPage. Only /auth was listed
  // here, and because "/signup" IS in RESERVED_PATHS its isPublicBioPage was
  // false too — so a logged-out visitor clicking "Sign up" from any marketing
  // page got the full dashboard shell: sidebar, account card, the lot, on the
  // conversion path. Auth pages render the card and nothing else.
  const isAuthPage = ["/auth", "/signup", "/login"].includes(location.pathname);
  const isLandingPage = location.pathname === "/";
  const isLegalPage = location.pathname === "/privacy" || location.pathname === "/terms" || location.pathname === "/data-deletion";
  const isBlogPage = location.pathname === "/blog" || location.pathname.startsWith("/blog/");
  // Public SEO marketing pages (no sidebar/mobile nav)
  const isMarketingPage =
    location.pathname === "/features" ||
    location.pathname.startsWith("/features/") ||
    location.pathname === "/compare" ||
    location.pathname.startsWith("/compare/") ||
    location.pathname.startsWith("/use-cases/") ||
    location.pathname === "/learn" ||
    location.pathname.startsWith("/learn/") ||
    // Top-level marketing pages (src/data/standalonePages.js). Without this
    // the page renders inside the app chrome and ships the dashboard sidebar
    // to crawlers — verify-prerender.js fails the build on exactly that.
    STANDALONE_SLUGS.some(
      (sl) => location.pathname === `/${sl}` || location.pathname.startsWith(`/${sl}/`),
    );
  // Track page views in Google Analytics on route change.
  //
  // Gated on the SAME allowlist the tag loader uses (published as
  // window.__tadloAnalyticsAllowed by public/index.html). Without the
  // re-check, someone landing on /blog — where the tag legitimately loads —
  // and then navigating client-side to a /:slug bio page or /dashboard
  // would still send a pageview for it, because window.gtag persists for
  // the life of the SPA. One list, tested in both places.
  //
  // FAILS CLOSED: if the helper is missing, no pageview is sent. A missing
  // guard must not mean "track everything".
  useEffect(() => {
    const allowed = window.__tadloAnalyticsAllowed;
    if (window.gtag && typeof allowed === "function" && allowed(location.pathname)) {
      window.gtag('config', 'G-YLKM93MEE7', { page_path: location.pathname + location.search });
    }
  }, [location.pathname, location.search]);

  const isBlogAdmin = location.pathname === "/td-admin-auth" || location.pathname === "/td-admin";
  const isVerifyPage = location.pathname === "/verify";
  // May 2026 — referral landing pages render their own dark shell;
  // skip the dashboard sidebar/nav wrapper.
  const isInvitePage = location.pathname.startsWith("/invite/");
  const isPublicBioPage = !isReservedPath(location.pathname);
  // Aug 2026 — pages that are listed in sitemap.xml and robots.txt as public
  // but were only ever reachable while logged in.
  //
  // /pricing and /subscription sat behind ProtectedRoute. /trends was not
  // protected, but rendering it inside the app chrome amounted to the same
  // thing: the sidebar/mobile nav mount authenticated pollers (eg. NotifBell
  // -> /notif/unread-count) and that 401 bounces an anonymous visitor to
  // /auth via the interceptor. Either way a crawler saw a login screen.
  //
  // For an anonymous visitor these now render bare. Signed-in users keep the
  // full app chrome, so the in-app upgrade and trends flows are unchanged.
  const isAnonymousPublicPage =
    !isAuthenticated &&
    ["/pricing", "/subscription", "/trends"].includes(location.pathname);
  // <MasterApp/> is a separate admin console that brings its own shell. It
  // rendered bare only because it was missing from RESERVED_PATHS; now that
  // it is correctly reserved, it needs to say so explicitly or it would
  // inherit the creator sidebar it has never had.
  const isMasterApp =
    location.pathname === "/master" || location.pathname.startsWith("/master/");

  // The public marketing surface gets the site nav — one mount here rather
  // than ten page components remembering to add it. Until now the nav existed
  // only inside LandingPage, so a reader arriving on an article from search
  // had no route to pricing, features or signup at all.
  //
  // Excluded deliberately: the homepage renders its own copy (it wraps the
  // whole document in .tdv4); auth, bio pages, /verify, /invite, the admin
  // consoles and the app itself must stay bare.
  const wantsMarketingNav =
    !isLandingPage &&
    !isBlogAdmin &&
    (isMarketingPage || isBlogPage || isLegalPage || isAnonymousPublicPage);

  if (isPublicBioPage || isAuthPage || isLandingPage || isLegalPage || isBlogPage || isBlogAdmin || isVerifyPage || isMarketingPage || isInvitePage || isAnonymousPublicPage || isMasterApp) {
    if (wantsMarketingNav) {
      return (
        <>
          <MarketingNav />
          {children}
        </>
      );
    }
    return children;
  }

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Growing Accounts Guidelines. Mounted here, inside the signed-in
          shell, so it covers every page a creator can reach — it has to
          be answered before anything else is usable. Renders nothing
          unless the server says this account must accept, which is Free
          plan under 100 followers and not yet accepted.
          Suspense fallback is null: while the chunk loads there is
          simply no modal, which is the same as the common case. */}
      <Suspense fallback={null}>
        <GrowingAccountsModal />
      </Suspense>
      {/* Desktop Sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <DesktopSidebar />
      </div>
      
      {/* Main Content - with bottom padding for mobile nav */}
      <main className="md:ml-64 min-h-[100dvh] pb-[calc(60px+env(safe-area-inset-bottom,0px))] md:pb-0">
        {children}
      </main>
      
      {/* Mobile Bottom Nav - visible only on mobile */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50">
        <MobileNav />
      </div>
    </div>
  );
}

/**
 * The app-wide error boundary that ErrorBoundary's own comment claimed
 * existed for months but did not.
 *
 * Until 2026-10-06 the ONLY <ErrorBoundary> in the app was around
 * <InboxPage />. Everywhere else, an uncaught render throw unmounted the
 * whole tree — and React 18's createRoot leaves #root empty when it
 * does, so the customer got a black screen rather than an error. A
 * Slovakian customer hit that on the checkout page on three devices and
 * could only report "the screen is black and nothing happens", because
 * there was genuinely nothing else to see.
 *
 * Keyed on the pathname via `resetKey` rather than React's `key`: `key`
 * would remount every route on every navigation, throwing away page
 * state the app relies on. `resetKey` only ever clears a fallback that
 * is already showing.
 */
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <ErrorBoundary label="this page" resetKey={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

function App() {
  return (
      <ThemeProvider>
        <AuthProvider>
          <FeatureAccessProvider>
          <BrowserRouter>
            <SupportProvider>
            {/* Emits noindex on the private surface. Must sit INSIDE
                BrowserRouter — it reads useLocation(). */}
            <RobotsPolicy />
            <ImpersonationBanner />
            <InstagramReconnectBanner />
            <AppLayout>
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
              <RemountOnAccountSwitch>
              <RouteErrorBoundary>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/data-deletion" element={<DataDeletionPage />} />
                <Route path="/blog" element={<BlogListPage />} />
                <Route path="/blog/:slug" element={<BlogArticlePage />} />
                <Route path="/td-admin-auth" element={<BlogAdminLoginPage />} />
                <Route path="/td-admin" element={<BlogAdminPage />} />
                <Route path="/master/*" element={<MasterApp />} />
                <Route path="/trends" element={<TrendsPage />} />
                <Route path="/verify" element={<VerifyEmailPage />} />
                {/* /signup — alias used by all marketing CTAs. Renders the
                    standard AuthPage in signup mode. */}
                <Route path="/signup" element={<AuthPage />} />
                {/* /login had NO route. ImpersonationBanner.jsx:50 redirects
                    here, which landed on the /:slug bio catch-all. */}
                <Route path="/login" element={<AuthPage />} />

                {/* May 2026 — Public referral landing. Visitors hit
                    /invite/{TOKEN} → see the offer → click into
                    /auth?mode=signup with the token persisted in
                    localStorage. Activation fires inside ``login()``
                    once auth completes. */}
                <Route path="/invite/:token" element={<InvitePage />} />

                {/* ════════ SEO Marketing Pages ════════
                    - /features/:slug → FeaturePage (12 content pages)
                    - /compare → CompareIndexPage (grid of 6 comparisons)
                    - /compare/vs-:slug → ComparePage (6 competitor pages) */}
                <Route path="/features" element={<FeaturesIndexPage />} />
                {/* The 38 multilingual SEO pages (src/content/multilingual).
                    Every slug is routed, published or not: an unpublished
                    one renders the 404 page instead of falling through to
                    FeaturePage / LearnPage, which redirect home. */}
                {MULTILINGUAL_SEO_SLUGS.map((s) => (
                  <Route key={s} path={`/${s}`} element={<MultilingualSeoPage slug={s} />} />
                ))}
                <Route path="/features/:slug" element={<FeaturePage />} />
                <Route path="/meta-approved" element={<StandalonePage />} />
                {/* /use-cases/:slug — two-segment, so it cannot collide with
                    a creator bio slug at /:slug. No /use-cases index page in
                    this batch, so the bare path stays unrouted rather than
                    claimed-but-empty. */}
                <Route path="/use-cases/:slug" element={<UseCasePage />} />
                {/* /learn — the answer-page cluster. The hub is routed as well
                    as the children, because all seven name /learn as the middle
                    step of their BreadcrumbList; an unrouted parent would fall
                    through to app-shell.html, whose canonical is the homepage. */}
                <Route path="/learn" element={<LearnIndexPage />} />
                <Route path="/learn/:slug" element={<LearnPage />} />
                <Route path="/compare" element={<CompareIndexPage />} />
                {/* MUST precede /compare/:slug — ComparePage redirects any
                    slug that does not start "vs-" to the homepage. */}
                <Route path="/compare/all-instagram-dm-tools" element={<ToolComparisonPage />} />
                <Route path="/compare/:slug" element={<ComparePage />} />

                {/* Protected Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                {/* Tadlo Editor — WYSIWYG writing editor */}
                <Route path="/editor" element={<ProtectedRoute><EditorListPage /></ProtectedRoute>} />
                <Route path="/editor/:id" element={<ProtectedRoute><EditorPage /></ProtectedRoute>} />
                <Route
                  path="/inbox"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary label="the inbox">
                        <InboxPage />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route path="/posts" element={<ProtectedRoute><PostsReelsPage /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPrefsPage /></ProtectedRoute>} />
                <Route
                  path="/automation"
                  element={
                    <ProtectedRoute>
                      <AutomationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/automation/results/:flowId"
                  element={
                    <ProtectedRoute>
                      <AutomationResultsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/automation/multilingual/*"
                  element={
                    <ProtectedRoute>
                      <MultilingualAutomationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contacts"
                  element={
                    <ProtectedRoute>
                      <ContactsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ab-testing"
                  element={
                    <ProtectedRoute>
                      {/* Locked — see config/comingSoon.js. The route stays
                          registered so a bookmark or shared link lands on an
                          explanation rather than a 404, but the feature
                          itself is not mounted. */}
                      <ComingSoonPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads"
                  element={
                    <ProtectedRoute>
                      <LeadsPage />
                    </ProtectedRoute>
                  }
                />
                {/* /collabs — Creator Marketplace (connect creators & brands) */}
                <Route
                  path="/collabs"
                  element={
                    <ProtectedRoute>
                      <CreatorMarketplacePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/comments"
                  element={
                    <ProtectedRoute>
                      <CommentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <AnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics/v4"
                  element={
                    <ProtectedRoute>
                      <AnalyticsV4Page />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics/legacy"
                  element={
                    <ProtectedRoute>
                      <AnalyticsLegacyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics/reel/:id"
                  element={
                    <ProtectedRoute>
                      <ReelDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics/reel/:id/ai-report"
                  element={
                    <ProtectedRoute>
                      <AIReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/analytics/reports/reel/:id" element={<ProtectedRoute><AIV5ReelReportPage /></ProtectedRoute>} />
                <Route path="/analytics/reports/weekly/:id" element={<ProtectedRoute><AIV5WeeklyReportPage /></ProtectedRoute>} />
                <Route
                  path="/products"
                  element={
                    <ProtectedRoute>
                      <ProductsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bio"
                  element={
                    <ProtectedRoute>
                      <BioEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                {/* /pricing is PUBLIC (Aug 2026). It sits in robots.txt's
                    Allow list and in sitemap.xml, but ProtectedRoute meant
                    every anonymous visitor — and every crawler — was bounced
                    to /auth, so the prices were never in any indexable
                    response. PricingPage already handles the logged-out case
                    (handleUpgrade/handleAddon route to /auth on click, the
                    usage/referral panels are behind `&&` guards, and the plan
                    list itself comes from the unauthenticated
                    GET /api/sub/plans). */}
                <Route path="/pricing" element={<PricingPage />} />
                {/* /subscription is an alias for /pricing — historical
                    deep-links + UpgradeNow CTAs reference the friendlier
                    URL. Keeps the public surface stable without
                    duplicating the full page component. Public for the same
                    reason /pricing is: a shared link should show the prices,
                    not a login wall. /subscription/success stays protected. */}
                <Route path="/subscription" element={<PricingPage />} />
                <Route
                  path="/subscription/success"
                  element={
                    <ProtectedRoute>
                      <SubscriptionSuccessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/connect-instagram"
                  element={
                    <ProtectedRoute>
                      <InstagramConnectPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/instagram/accounts"
                  element={
                    <ProtectedRoute>
                      <ManageAccountsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-writer"
                  element={
                    <ProtectedRoute>
                      {/* Locked — see config/comingSoon.js. The route stays
                          registered so a bookmark or shared link lands on an
                          explanation rather than a 404, but the feature
                          itself is not mounted. */}
                      <ComingSoonPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/growth-agent"
                  element={
                    <ProtectedRoute>
                      <GrowthAgentPage />
                    </ProtectedRoute>
                  }
                />
                {/* TEMPORARY Instagram Graph API debug panel — hidden route.
                    Not linked from sidebar. Remove when integration is debugged. */}
                <Route
                  path="/debug-instagram"
                  element={
                    <ProtectedRoute>
                      <DebugInstagramPage />
                    </ProtectedRoute>
                  }
                />
                
                {/* Static/SEO endpoints — proxied to backend via /api/ because
                    Kubernetes ingress only routes /api/* to the backend. Uses
                    full-page navigation (not SPA routing) so the browser
                    receives the raw XML/text response, not the React shell. */}
                <Route
                  path="/sitemap.xml"
                  element={<ExternalRedirect to="/api/sitemap.xml" />}
                />
                <Route
                  path="/robots.txt"
                  element={<ExternalRedirect to="/api/robots.txt" />}
                />

                {/* Public Bio Page (catch-all for /:username - must be last) */}
                <Route path="/:slug" element={<PublicBioPage />} />
                
                {/* Fallback — anything the single-segment bio route above
                    cannot match (/abc/xyz). This used to
                    `<Navigate to="/" replace />`, so a mistyped nested URL
                    silently became the marketing homepage and the visitor
                    was never told the address was wrong. */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </RouteErrorBoundary>
              </RemountOnAccountSwitch>
              </Suspense>
            </AppLayout>
            <OnboardingProvider />
            {/* Stale-bundle detector. Inside BrowserRouter because it checks
                on navigation; renders nothing until a newer build exists. */}
            <NewVersionToast />
            <Toaster position="top-center" richColors />
            </SupportProvider>
          </BrowserRouter>
          </FeatureAccessProvider>
        </AuthProvider>
      </ThemeProvider>
  );
}

export default App;
